import { format } from "date-fns";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { TagSelector } from "./TagSelector";
import { CalendarIcon, Loader2, Plus } from "lucide-react";

/**
 * "Add New Prayer" modal — extracted from DashboardPage to keep that page
 * under 800 lines and make the form independently testable.
 *
 * Stateless: form state and the submit handler are owned by the parent.
 */
export function NewPrayerDialog({
  open, onOpenChange,
  prayerForm, setPrayerForm,
  groups,
  formLoading,
  onSubmit,
  onClose,
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => { onOpenChange(o); if (!o) onClose?.(); }}
    >
      <DialogTrigger asChild>
        <Button className="bg-sage hover:bg-sage-dark text-white rounded-full" data-testid="new-prayer-btn">
          <Plus className="w-4 h-4 sm:mr-2" />
          <span className="hidden sm:inline">New Prayer</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif">Add New Prayer</DialogTitle>
          <DialogDescription>Share what's on your heart</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="request">Prayer Request *</Label>
            <Textarea
              id="request"
              placeholder="What would you like to pray for?"
              value={prayerForm.request}
              onChange={(e) => setPrayerForm({ ...prayerForm, request: e.target.value })}
              required
              className="min-h-24"
              data-testid="prayer-request-input"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="requested_by">Requested By</Label>
              <Input
                id="requested_by"
                placeholder="Who is this prayer for?"
                value={prayerForm.requested_by}
                onChange={(e) => setPrayerForm({ ...prayerForm, requested_by: e.target.value })}
                data-testid="prayer-requested-by"
              />
            </div>
            <div className="space-y-2">
              <Label>Date Requested</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                    data-testid="prayer-date-requested"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {prayerForm.date_requested ? format(prayerForm.date_requested, "PPP") : "Select date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={prayerForm.date_requested}
                    onSelect={(date) => setPrayerForm({ ...prayerForm, date_requested: date })}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="notes">Notes (optional)</Label>
            <Textarea
              id="notes"
              placeholder="Any additional details..."
              value={prayerForm.notes}
              onChange={(e) => setPrayerForm({ ...prayerForm, notes: e.target.value })}
              className="min-h-16"
              data-testid="prayer-notes-input"
            />
          </div>

          <TagSelector
            selectedTags={prayerForm.tags}
            onChange={(tags) => setPrayerForm({ ...prayerForm, tags })}
            testIdPrefix="new-prayer-"
          />

          {groups.length > 0 && (
            <div className="space-y-2">
              <Label>Add to Group (optional)</Label>
              <Select
                value={prayerForm.group_id || "none"}
                onValueChange={(value) =>
                  setPrayerForm({ ...prayerForm, group_id: value === "none" ? "" : value })
                }
              >
                <SelectTrigger data-testid="prayer-group-select">
                  <SelectValue placeholder="Personal prayer (no group)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Personal prayer (no group)</SelectItem>
                  {groups.map((group) => (
                    <SelectItem key={group.id} value={group.id}>{group.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-stone-dark/50">
                Group prayers are shared with all group members
              </p>
            </div>
          )}

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline" className="rounded-full">Cancel</Button>
            </DialogClose>
            <Button
              type="submit"
              className="bg-sage hover:bg-sage-dark text-white rounded-full"
              disabled={formLoading}
              data-testid="submit-prayer-btn"
            >
              {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Add Prayer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
